import { keyframes } from 'goober';
import styled, { css } from 'styled-components';

import { bottomNavHeight } from '../../../components/layout/BottomNav/BottomNav.styles';
import ZIndexLayers from '../../../services/ZIndexLayers';
import { ThemeProp } from '../../../theme/Themes';

interface ContainerProps {
  $visible: boolean;
  $active?: boolean;
  $offsetY: number;
  $animated?: boolean;
  $isBottomBarVisible?: boolean;
}

const animationDuration = '0.23s';

const enterAnimation = (offset: number) => `
  0% { transform: translate3d(0, 200%, 0); opacity: .5; }
  100% { transform: translate3d(0, ${offset}, 0); opacity: 1 ;}
`;

const exitAnimation = (offset: number) => `
  0% { transform: translate3d(0, ${offset}, -1px); opacity: 1; }
  100% { transform: translate3d(0, -300%, -1px); opacity: 0; }
`;

// NOTE: Design Specs
const shadowStyle = '0px 4px 8px 0px rgba(0, 0, 0, 0.08), 0px 1px 2px 0px rgba(0, 0, 0, 0.08)';

const getAnimationStyle = (props: ContainerProps): string => {
  return props.$visible
    ? `${keyframes(enterAnimation(props.$offsetY))} ${animationDuration} ease-in forwards`
    : `${keyframes(exitAnimation(props.$offsetY))} ${animationDuration} forwards ease-in`;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: ${(props: ContainerProps) => (props.$active ? '21.875rem' : '21rem')};
  max-height: ${(props: ContainerProps) => (props.$active ? '20rem' : '4rem')};
  margin-left: ${(props: ContainerProps) => (props.$active ? '0' : '0.5rem')};
  border-radius: 0.5rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  background-color: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  box-shadow: ${shadowStyle};
  transition: all ${animationDuration} ease-in;
  bottom: 0;
  transform: ${(props: ContainerProps) => `translateY(${-props.$offsetY}px)`};
  padding: 1rem;
  z-index: ${ZIndexLayers.overModal};
  animation: ${(props: ContainerProps) => (props.$animated ? getAnimationStyle(props) : 'none')};
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    margin-left: 5vw;
    width: 90vw;
    bottom: ${(props: ContainerProps) =>
      props.$isBottomBarVisible ? bottomNavHeight : 'calc(env(safe-area-inset-bottom, 1rem))'};
  }
`;

interface ContentContainerProps {
  $isCustom?: boolean;
}

export const ContentContainer = styled.div<ContentContainerProps>`
  display: flex;
  flex-direction: ${(props: ContentContainerProps) => (props.$isCustom ? 'column' : 'row')};
  width: 100%;
  overflow: hidden;
  gap: 0.5rem;
`;

export const MessageContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow: hidden;
  width: 100%;
`;

export const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  overflow: hidden;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
`;

export const MessageText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: 150%;
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  overflow: hidden;
  word-break: break-word;
`;

export const DismissButton = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: 1rem;
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
  cursor: pointer;
  :hover {
    color: ${(props: ThemeProp) => props.theme.colors.main.c600};
  }
`;

interface IconContainerProps {
  $type?: string;
}

export const IconContainer = styled.div<IconContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: ${(props: IconContainerProps & ThemeProp) =>
    props.$type === 'error' ? props.theme.colors.layout.surface.error : props.theme.colors.layout.surface.success};
  stroke: ${(props: IconContainerProps & ThemeProp) =>
    props.$type === 'error' ? props.theme.colors.content.error : props.theme.colors.content.success};
`;

interface CloseButtonProps {
  $expandable?: boolean;
}

export const CloseButton = styled.div<CloseButtonProps>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.5rem;
  top: -0.75rem;
  right: -0.75rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  border-radius: 4.625rem;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  box-shadow: ${shadowStyle};
  cursor: pointer;
  svg {
    width: 1.45rem;
    height: 1.45rem;
    stroke: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
  }
  span {
    max-width: 0;
    transition: max-width 1s;
    transition-delay: 0.25s;
    display: inline-block;
    vertical-align: top;
    white-space: nowrap;
    overflow: hidden;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
    :after {
      content: '\\2003';
    }
  }
  ${(props: CloseButtonProps) =>
    props.$expandable &&
    css`
      :hover {
        span {
          max-width: 7rem;
        }
      }
    `};
`;

export const MoreNotificationsText = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption2.lineHeight};
`;

export const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
